import React, { useState } from 'react'
import {
  Box,
  TextField,
  Chip,
  Grid,
  Autocomplete,
  Checkbox,
  Avatar,
  ListItemAvatar,
  Typography
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import _ from 'lodash'
import CPButton from '../../../Utils/CPButton'

const EMAIL_RE = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/

export default function DocAlertRecipientsForm({
  recipients,
  suggestions,
  onAdd,
  onRemove
}) {
  const { t } = useTranslation()
  const [freeEmail, setFreeEmail] = useState('')
  const [freeEmailError, setFreeEmailError] = useState(false)

  const handleAddFreeEmail = () => {
    if (!EMAIL_RE.test(freeEmail)) {
      setFreeEmailError(true)
      return
    }
    if (recipients.find((r) => r.email === freeEmail)) {
      setFreeEmail('')
      return
    }
    setFreeEmailError(false)
    onAdd({
      recipient_type: 'specific_email',
      email: freeEmail,
      is_excluded: false,
      _local_id: Date.now()
    })
    setFreeEmail('')
  }

  const handleToggleSuggestion = (checked, user) => {
    if (checked) {
      onAdd({
        recipient_type: 'specific_user',
        user_id: user.id,
        email: user.email,
        is_excluded: false,
        _local_id: user.id
      })
    } else {
      const existing = recipients.find((r) => r.user_id === user.id)
      if (existing) onRemove(existing._local_id)
    }
  }

  const recipientLabel = (r) => (r.user_name ? `${r.user_name}` : r.email || '')

  return (
    <Box>
      <Grid container spacing={2}>
        {suggestions.length > 0 && (
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={suggestions}
              disableCloseOnSelect
              disableClearable
              getOptionLabel={(u) => `${u.first_name} ${u.last_name}`}
              value={suggestions.filter((u) =>
                _.find(recipients, (r) => r.user_id === u.id)
              )}
              ChipProps={{ onDelete: null }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                    checkedIcon={<CheckBoxIcon fontSize='small' />}
                    sx={{ mr: 1 }}
                    checked={
                      !!_.find(recipients, (r) => r.user_id === option.id)
                    }
                    onChange={(e) =>
                      handleToggleSuggestion(e.target.checked, option)
                    }
                  />
                  <ListItemAvatar>
                    {option.image_url ? (
                      <Avatar
                        src={option.image_url}
                        sx={{ width: 28, height: 28 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 28, height: 28 }}>
                        {option.first_name[0]}
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  {`${option.first_name} ${option.last_name} | ${option.email}`}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label={t('Send to')} size='small' />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              size='small'
              label={t('Add email address')}
              value={freeEmail}
              error={freeEmailError}
              helperText={freeEmailError ? t('Invalid email address') : ''}
              onChange={(e) => {
                setFreeEmail(e.target.value)
                setFreeEmailError(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddFreeEmail()
                }
              }}
            />
            <CPButton
              icon={<AddIcon />}
              tooltip={t('Add')}
              onClick={handleAddFreeEmail}
            />
          </Box>
        </Grid>

        {recipients.length > 0 && (
          <Grid item xs={12}>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ mb: 0.5, display: 'block' }}
            >
              {t('Recipients')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {recipients.map((r) => (
                <Chip
                  key={r._local_id}
                  label={recipientLabel(r)}
                  size='small'
                  onDelete={() => onRemove(r._local_id)}
                />
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
